import { useEffect, useState } from 'react';
import { Badge, Button, Spinner } from '@klaro7/ui';

import { usePinGate } from '@/components/PinGate';
import {
  usePhoneLookupSettings,
  useUpdatePhoneLookupSettings,
  usePhoneLookupStats,
} from '../hooks/useServicesSettings';

function extractErrorDetail(err: unknown): string {
  if (err && typeof err === 'object' && 'response' in err) {
    const resp = err as {
      response?: {
        data?: { error?: { message?: string }; message?: string };
        status?: number;
        statusText?: string;
      };
    };
    const apiMsg = resp.response?.data?.error?.message || resp.response?.data?.message;
    const status = resp.response?.status;
    if (apiMsg) return `[${status}] ${apiMsg}`;
    if (status) return `HTTP ${status}: ${resp.response?.statusText || 'Errore sconosciuto'}`;
  }
  if (err instanceof Error) return err.message;
  return String(err);
}

/**
 * Rilevamento VoIP/numeri virtuali (Twilio Lookup, super-admin only). Riusa
 * le credenziali Twilio Voice già configurate — nessuna chiave qui, solo
 * un toggle e il consumo mensile stimato (per tenere sotto controllo il
 * costo: $0.008/richiesta, chiamato solo a OTP confermato).
 */
export function PhoneLookupSettingsSection() {
  const { requirePin, pinDialog } = usePinGate();
  const { data: settings, isLoading } = usePhoneLookupSettings(true);
  const { data: stats, isLoading: statsLoading } = usePhoneLookupStats(true);
  const updateMutation = useUpdatePhoneLookupSettings();

  const [enabled, setEnabled] = useState(false);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  useEffect(() => {
    if (settings) setEnabled(settings.enabled ?? false);
  }, [settings]);

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), toast.type === 'error' ? 15000 : 4000);
    return () => clearTimeout(timer);
  }, [toast]);

  async function handleSave() {
    try {
      await updateMutation.mutateAsync({ enabled });
      setToast({ type: 'success', message: 'Configurazione Phone Lookup salvata con successo' });
    } catch (err) {
      setToast({ type: 'error', message: `Errore nel salvataggio Phone Lookup: ${extractErrorDetail(err)}` });
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white">
      <div className="flex items-center justify-between border-b border-neutral-100 bg-neutral-50 px-5 py-3.5">
        <div className="flex items-center gap-3">
          <svg className="h-5 w-5 text-neutral-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10.5 1.5H8.25A2.25 2.25 0 006 3.75v16.5a2.25 2.25 0 002.25 2.25h7.5A2.25 2.25 0 0018 20.25V3.75a2.25 2.25 0 00-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 18.75h3" />
          </svg>
          <div>
            <h3 className="text-sm font-semibold text-neutral-700">Rilevamento VoIP (Twilio Lookup)</h3>
            <p className="text-xs text-neutral-600">Blocca il bonus benvenuto su numeri virtuali/rent-a-SMS (solo super admin)</p>
          </div>
        </div>
        <Badge variant={enabled ? 'success' : 'default'}>
          {enabled ? 'Attivo' : 'Disattivato'}
        </Badge>
      </div>

      <div className="space-y-4 p-5">
        {toast && (
          <div
            className={`rounded-lg border px-4 py-2.5 text-sm font-medium ${
              toast.type === 'success'
                ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                : 'border-red-200 bg-red-50 text-red-700'
            }`}
          >
            {toast.message}
          </div>
        )}

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            className="h-4 w-4 rounded border-neutral-300 text-brand-teal focus:ring-brand-teal"
          />
          Abilitato
        </label>

        <p className="rounded-lg border border-neutral-100 bg-neutral-50 px-4 py-3 text-xs text-neutral-600">
          Chiamato una sola volta, a OTP confermato (non ad ogni registrazione) —
          riusa Account SID/Auth Token da Twilio Voice qui sopra. Numeri VoIP,
          virtuali o rent-a-SMS non ricevono il bonus di benvenuto; il numero
          resta comunque verificato e l&apos;utente può ricaricare e chiamare
          normalmente.
        </p>

        <div className="rounded-lg border border-neutral-100 p-4">
          <div className="mb-3 flex items-center justify-between">
            <h4 className="text-sm font-semibold text-neutral-700">Consumo stimato questo mese</h4>
            {stats && (
              <span className="text-sm font-semibold text-neutral-900">
                ${stats.total.estCostUsd.toFixed(2)} · {stats.total.count} richieste
              </span>
            )}
          </div>
          {statsLoading ? (
            <Spinner size="sm" />
          ) : stats && stats.items.length > 0 ? (
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-neutral-500">
                  <th className="pb-1 font-medium">Tipo linea</th>
                  <th className="pb-1 font-medium">Richieste</th>
                  <th className="pb-1 font-medium">Costo stimato</th>
                </tr>
              </thead>
              <tbody>
                {stats.items.map((item) => (
                  <tr key={item.lineType} className="border-t border-neutral-100">
                    <td className="py-1.5 text-neutral-700">{item.lineType}</td>
                    <td className="py-1.5 text-neutral-700">{item.count}</td>
                    <td className="py-1.5 text-neutral-700">${item.estCostUsd.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-xs text-neutral-500">Nessuna richiesta questo mese.</p>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-neutral-100 pt-4">
          {pinDialog}
          <Button onClick={() => requirePin(handleSave)} loading={updateMutation.isPending}>
            Salva Phone Lookup
          </Button>
        </div>
      </div>
    </div>
  );
}
